import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolInvoke } from '@saas-pages/shared/public/runtime'
import { numberLocale, useLocale, useT, type Translations } from '@/shared/i18n'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'
import { CodeWorkspace } from '@/shared/ui/code-workspace'

const UTILITY = 'base64'

type Mode = 'encode' | 'decode'
type Base64Variant = 'standard' | 'standard-no-padding' | 'url-safe' | 'url-safe-no-padding'

const BASE64_VARIANTS: Base64Variant[] = [
  'standard',
  'standard-no-padding',
  'url-safe',
  'url-safe-no-padding',
]

const BASE64_VARIANT_CONFIG: Record<Base64Variant, { urlSafe: boolean; noPadding: boolean }> = {
  standard: { urlSafe: false, noPadding: false },
  'standard-no-padding': { urlSafe: false, noPadding: true },
  'url-safe': { urlSafe: true, noPadding: false },
  'url-safe-no-padding': { urlSafe: true, noPadding: true },
}

function isUrlSafeVariant(variant: Base64Variant): boolean {
  return BASE64_VARIANT_CONFIG[variant].urlSafe
}

function isNoPaddingVariant(variant: Base64Variant): boolean {
  return BASE64_VARIANT_CONFIG[variant].noPadding
}

function detectBase64Variant(str: string): Base64Variant | null {
  const cleaned = str.replace(/\s/g, '')
  if (!cleaned || cleaned.length % 4 === 1) return null

  const hasStandardSymbols = /[+/]/.test(cleaned)
  const hasUrlSafeSymbols = /[-_]/.test(cleaned)
  if (hasStandardSymbols && hasUrlSafeSymbols) return null

  const standardRegex = /^[A-Za-z0-9+/]*={0,2}$/
  const urlSafeRegex = /^[A-Za-z0-9_-]*={0,2}$/
  const isNoPadding = !cleaned.endsWith('=') && cleaned.length % 4 !== 0

  if (hasUrlSafeSymbols) {
    if (!urlSafeRegex.test(cleaned)) return null
    return isNoPadding ? 'url-safe-no-padding' : 'url-safe'
  }

  if (!standardRegex.test(cleaned)) return null
  return isNoPadding ? 'standard-no-padding' : 'standard'
}

function isBase64Candidate(str: string): boolean {
  if (str.length < 4) return false
  const detectedVariant = detectBase64Variant(str)
  if (!detectedVariant) return false
  if (str.endsWith('==') || str.endsWith('=')) return true
  return str.length > 8 && /[A-Za-z0-9+/_-]{4,}/.test(str)
}

function encodeBase64(text: string, t: Translations, variant: Base64Variant): string {
  try {
    const encoder = new TextEncoder()
    const bytes = encoder.encode(text)
    let binary = ''
    for (const byte of bytes) {
      binary += String.fromCharCode(byte)
    }
    let result = btoa(binary)
    if (isUrlSafeVariant(variant)) {
      result = result.replace(/\+/g, '-').replace(/\//g, '_')
    }
    if (isNoPaddingVariant(variant)) {
      result = result.replace(/=+$/, '')
    }
    return result
  } catch {
    throw new Error(t.base64.errors.encode)
  }
}

function normalizeBase64ForDecode(base64: string, variant: Base64Variant, t: Translations): string {
  const cleaned = base64.replace(/\s/g, '')
  if (!cleaned) throw new Error(t.base64.errors.emptyDecode)

  const regex = isUrlSafeVariant(variant)
    ? /^[A-Za-z0-9_-]*={0,2}$/
    : /^[A-Za-z0-9+/]*={0,2}$/

  if (!regex.test(cleaned)) {
    throw new Error(t.base64.errors.invalid)
  }

  const existingPadding = cleaned.match(/=+$/)?.[0].length ?? 0

  if (isNoPaddingVariant(variant) && existingPadding > 0) {
    throw new Error(t.base64.errors.invalid)
  }

  const standardBase64 = isUrlSafeVariant(variant)
    ? cleaned.replace(/-/g, '+').replace(/_/g, '/')
    : cleaned
  const unpaddedBase64 = standardBase64.replace(/=+$/, '')
  if (unpaddedBase64.length % 4 === 1) {
    throw new Error(t.base64.errors.invalid)
  }

  const requiredPadding = (4 - (unpaddedBase64.length % 4)) % 4
  if (existingPadding > 0 && existingPadding !== requiredPadding) {
    throw new Error(t.base64.errors.invalid)
  }

  return unpaddedBase64 + '='.repeat(requiredPadding)
}

function decodeBase64(base64: string, t: Translations, variant: Base64Variant): string {
  try {
    const binary = atob(normalizeBase64ForDecode(base64, variant, t))
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i)
    }
    const decoder = new TextDecoder('utf-8', { fatal: true })
    return decoder.decode(bytes)
  } catch {
    throw new Error(t.base64.errors.invalidUtf8)
  }
}

export function Base64Page() {
  const t = useT()
  const { locale } = useLocale()
  const localeCode = numberLocale(locale)
  const [input, setInput] = useState('')
  const [mode, setMode] = useState<Mode>('encode')
  const [variant, setVariant] = useState<Base64Variant>('standard')
  const [output, setOutput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const handleModeChange = useCallback((newMode: Mode) => {
    setMode(newMode)
    setOutput('')
    setError(null)
  }, [])

  const handleVariantChange = useCallback((newVariant: Base64Variant) => {
    setVariant(newVariant)
    setOutput('')
    setError(null)
  }, [])

  const handleProcess = useCallback((explicitMode?: Mode, explicitVariant?: Base64Variant) => {
    if (!input) {
      setOutput('')
      setError(null)
      return
    }

    const activeMode = explicitMode ?? mode
    const activeVariant = explicitVariant ?? variant
    setError(null)
    toolInvoke(UTILITY, { mode: activeMode, variant: activeVariant })

    try {
      if (activeMode === 'encode') {
        const result = encodeBase64(input, t, activeVariant)
        setOutput(result)
      } else {
        const result = decodeBase64(input, t, activeVariant)
        setOutput(result)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t.base64.errors.unknown
      setError(message)
      setOutput('')
    }
  }, [input, mode, t, variant])

  useEffect(() => {
    const timeout = window.setTimeout(() => handleProcess(), 200)
    return () => window.clearTimeout(timeout)
  }, [handleProcess])

  const handleAutoDetect = useCallback(() => {
    if (!input) return
    const detectedMode: Mode = isBase64Candidate(input) ? 'decode' : 'encode'
    const detectedVariant = detectBase64Variant(input)
    if (detectedMode === 'decode' && detectedVariant && detectedVariant !== variant) {
      setVariant(detectedVariant)
    }
    if (detectedMode !== mode) {
      handleModeChange(detectedMode)
    }
  }, [input, mode, variant, handleModeChange])

  const handleCopy = useCallback(async () => {
    if (!output) return
    try {
      await navigator.clipboard.writeText(output)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }, [output])

  const handleSwap = useCallback(() => {
    const newMode = mode === 'encode' ? 'decode' : 'encode'
    setInput(output)
    setMode(newMode)
    setOutput('')
    setError(null)
  }, [mode, output])

  const handleClear = useCallback(() => {
    setInput('')
    setOutput('')
    setError(null)
  }, [])

  const detectedModeHint = useMemo(() => {
    if (!input || input.length < 4) return null
    if (isBase64Candidate(input)) return t.base64.autoHint
    return null
  }, [input, t.base64.autoHint])

  const inputStats = useMemo(() => {
    return {
      chars: input.length,
      lines: input ? input.split('\n').length : 0,
    }
  }, [input])

  const outputStats = useMemo(() => {
    return {
      chars: output.length,
      lines: output ? output.split('\n').length : 0,
    }
  }, [output])

  return (
    <ToolPageShell toolKey="base64" width="wide">
      <CodeWorkspace
        error={error}
        controls={(
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium tracking-normal">{t.base64.mode}</span>
              <div className="flex rounded-lg border bg-card p-0.5 shadow-sm">
                <button
                  type="button"
                  onClick={() => handleModeChange('encode')}
                  className={cn(
                    'rounded-md px-4 py-1.5 text-sm font-medium transition-colors',
                    mode === 'encode'
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t.base64.encode}
                </button>
                <button
                  type="button"
                  onClick={() => handleModeChange('decode')}
                  className={cn(
                    'rounded-md px-4 py-1.5 text-sm font-medium transition-colors',
                    mode === 'decode'
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t.base64.decode}
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium tracking-normal">{t.base64.format}</span>
              <div className="flex flex-wrap rounded-lg border bg-card p-0.5 shadow-sm">
                {BASE64_VARIANTS.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => handleVariantChange(item)}
                    className={cn(
                      'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                      variant === item
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {t.base64.variants[item]}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {input && detectedModeHint && (
                <span className="rounded-md border bg-card px-2.5 py-1 text-xs text-muted-foreground">
                  {detectedModeHint}
                </span>
              )}
              <Button
                variant="secondary"
                onClick={handleAutoDetect}
                disabled={!input}
              >
                {t.base64.auto}
              </Button>
            </div>
          </div>
        )}
        input={{
          title: mode === 'encode' ? t.base64.sourceText : t.base64.base64String,
          value: input,
          onChange: (value) => {
            setInput(value)
            setOutput('')
            setError(null)
          },
          placeholder: mode === 'encode'
            ? t.base64.inputEncodePlaceholder
            : t.base64.inputDecodePlaceholder,
          ariaLabel: mode === 'encode' ? t.base64.encodeLabel : t.base64.decodeLabel,
          clear: { label: t.base64.clear, onClick: handleClear },
          stats: [
            {
              label: t.base64.inputLength,
              value: inputStats.chars.toLocaleString(localeCode),
            },
            {
              label: t.base64.lines,
              value: inputStats.lines.toLocaleString(localeCode),
            },
          ],
        }}
        output={{
          title: mode === 'decode' ? t.base64.decodedText : t.base64.resultTitle,
          value: error ? '' : output,
          readOnly: true,
          placeholder: t.base64.outputPlaceholder,
          ariaLabel: t.base64.resultLabel,
          actions: (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopy}
              >
                {copied ? t.base64.copied : t.base64.copy}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSwap}
              >
                {t.base64.swap}
              </Button>
            </>
          ),
          stats: output && !error
            ? [
              {
                label: t.base64.resultLength,
                value: outputStats.chars.toLocaleString(localeCode),
              },
              {
                label: t.base64.lines,
                value: outputStats.lines.toLocaleString(localeCode),
              },
            ]
            : undefined,
        }}
      />

      {/* Info section */}
      {output && (
        <section className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm font-medium tracking-normal">{t.base64.inputLength}</div>
              <div className="mt-1 text-2xl font-bold tabular-nums">
                {input.length.toLocaleString(localeCode)}
              </div>
              <div className="text-xs text-muted-foreground">
                {mode === 'encode' ? t.base64.characters : t.base64.base64Chars}
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm font-medium tracking-normal">{t.base64.resultLength}</div>
              <div className="mt-1 text-2xl font-bold tabular-nums">
                {output.length.toLocaleString(localeCode)}
              </div>
              <div className="text-xs text-muted-foreground">
                {mode === 'encode' ? t.base64.base64Chars : t.base64.characters}
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm font-medium tracking-normal">{t.base64.sourceSize}</div>
              <div className="mt-1 text-2xl font-bold tabular-nums">
                {(() => {
                  try {
                    const encoder = new TextEncoder()
                    return `${encoder.encode(input).length.toLocaleString(localeCode)} B`
                  } catch {
                    return '—'
                  }
                })()}
              </div>
              <div className="text-xs text-muted-foreground">UTF-8</div>
            </CardContent>
          </Card>
        </section>
      )}

      <section className="mt-10 space-y-5 border-t pt-8">
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.base64.howTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.base64.how}
          </p>
        </div>
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.base64.whenTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.base64.when}
          </p>
        </div>
      </section>
    </ToolPageShell>
  )
}
